import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, Sun, Moon } from "lucide-react";
import { Logo } from "./Logo";

const LINKS = [
  { id: "#expertises", label: "Expertises", testid: "nav-expertises-link" },
  { id: "#profil", label: "Profil", testid: "nav-profil-link" },
  { id: "#prestations", label: "Prestations", testid: "nav-prestations-link" },
  { id: "#faq", label: "FAQ", testid: "nav-faq-link" },
];

export const scrollToId = (e, id) => {
  e.preventDefault();
  if (window.__lenis) {
    window.__lenis.scrollTo(id, { offset: -72, duration: 1.4 });
  } else {
    document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });
  }
};

export const ThemeToggle = ({ compact = false }) => {
  const [light, setLight] = useState(
    () => localStorage.getItem("alc-theme") !== "dark"
  );

  useEffect(() => {
    document.documentElement.classList.toggle("light", light);
    localStorage.setItem("alc-theme", light ? "light" : "dark");
  }, [light]);

  return (
    <button
      type="button"
      data-testid="theme-toggle"
      onClick={() => setLight(!light)}
      aria-label={light ? "Passer au thème sombre" : "Passer au thème clair"}
      title={light ? "Version sombre" : "Version claire"}
      className={`group flex items-center gap-2.5 border border-ink-line text-mute transition-colors duration-300 hover:border-gold/60 hover:text-gold ${
        compact ? "h-10 w-10 justify-center" : "px-4 py-2.5"
      }`}
    >
      {light ? (
        <Moon className="h-4 w-4 transition-transform duration-500 group-hover:-rotate-12" />
      ) : (
        <Sun className="h-4 w-4 transition-transform duration-500 group-hover:rotate-45" />
      )}
      {!compact && (
        <span className="font-mono text-[10px] uppercase tracking-[0.22em]">
          {light ? "Sombre" : "Clair"}
        </span>
      )}
    </button>
  );
};

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, id) => {
    scrollToId(e, id);
    setOpen(false);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
        scrolled
          ? "border-b border-ink-line bg-ink/80 backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-6 lg:px-12">
        <a href="#top" onClick={(e) => go(e, "#top")} aria-label="ALMALEX Legal Consulting — accueil">
          <Logo />
        </a>

        <nav className="hidden items-center gap-9 lg:flex" aria-label="Navigation principale">
          {LINKS.map((l) => (
            <a
              key={l.id}
              href={l.id}
              data-testid={l.testid}
              onClick={(e) => go(e, l.id)}
              className="group relative font-mono text-xs uppercase tracking-[0.2em] text-mute transition-colors duration-300 hover:text-ivory"
            >
              {l.label}
              <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-gold transition-all duration-300 group-hover:w-full" />
            </a>
          ))}
          <ThemeToggle compact />
          <a
            href="#contact"
            data-testid="header-contact-cta"
            onClick={(e) => go(e, "#contact")}
            className="group flex items-center gap-2 border border-gold/60 px-5 py-2.5 font-mono text-xs uppercase tracking-[0.2em] text-gold transition-colors duration-300 hover:bg-gold hover:text-ink"
          >
            Prendre contact
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </nav>

        <div className="flex items-center gap-2.5 lg:hidden">
          <ThemeToggle compact />
          <button
            data-testid="mobile-menu-button"
            onClick={() => setOpen(!open)}
            className="flex h-10 w-10 items-center justify-center border border-ink-line text-ivory"
            aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-b border-ink-line bg-ink/95 backdrop-blur-xl lg:hidden"
            aria-label="Navigation mobile"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {LINKS.map((l) => (
                <a
                  key={l.id}
                  href={l.id}
                  data-testid={`${l.testid}-mobile`}
                  onClick={(e) => go(e, l.id)}
                  className="border-b border-ink-line/60 py-4 font-serif text-2xl text-ivory"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="#contact"
                data-testid="header-contact-cta-mobile"
                onClick={(e) => go(e, "#contact")}
                className="mt-4 flex items-center justify-center gap-2 border border-gold/60 px-5 py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-gold"
              >
                Prendre contact
                <ArrowUpRight className="h-3.5 w-3.5" />
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
